import type React from 'react';

import { withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/uk-surging-card/uk-surging-card.yml';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';
import { isError, isLoading } from '#src/app/shared/utils/async';

import { SurgingPetitionCard } from './components/SurgingPetitionCard';
import { FEATURED_PETITION_SLUG } from './constants';
import { useFeaturedPetition } from './hooks/useFeaturedPetition';

// Mounts only for `variation_1` on an eligible page (see container). Owns the live fetch and the
// render-gate: every suppressed outcome returns `null` — no skeleton, no error UI, zero footprint.
const FeaturedPetitionCard: React.FC = () => {
	const {
		data: { featuredPetitionState },
	} = useFeaturedPetition();

	if (isLoading(featuredPetitionState)) return null;
	if (isError(featuredPetitionState)) return null;

	const { petition } = featuredPetitionState;
	if (!petition) return null;
	if (petition.status !== 'PUBLISHED') return null;

	return (
		<div data-testid="uk-surging-card-wrapper" className="col-span-full container mt-6 lg:col-start-2 lg:col-end-11">
			<SurgingPetitionCard petition={petition} />
		</div>
	);
};

const UkSurgingPetitionCardContainerComponent: React.FC = () => {
	const {
		data: {
			petition: { slug: hostSlug },
		},
	} = usePetitionDetailsPageData();
	const {
		data: {
			experiments: { petitionPageUkSurgingCard: experiment },
		},
	} = usePetitionDetailsPageUserData();

	// Own-page relevance gate (client-only relevance): the featured petition's own page never shows
	// the card and never fires an exposure — return before any tracker or fetch.
	if (hostSlug === FEATURED_PETITION_SLUG) return null;

	// Exposure fires unconditionally for both arms on every eligible view — a sibling of the
	// variation branch, decoupled from the featured-petition fetch outcome (the helper self-no-ops
	// on fallback/override/empty name, so it is the single source of truth for "real assignment").
	return (
		<>
			<TrackExperimentExposure experiment={experiment} />
			{experiment.variation === 'variation_1' ? <FeaturedPetitionCard /> : null}
		</>
	);
};

export const UkSurgingPetitionCardContainer = withI18nPlaceholders(
	I18N_PLACEHOLDERS,
	UkSurgingPetitionCardContainerComponent,
);
